// js/utils/api.js
import { Toast } from '../comun/toast.js';

const TIMEOUT_MS = 10000; // 10 segundos configurados

// Una sola renovación en vuelo a la vez.
//
// El portal tiene varias llamadas simultáneas por diseño: el ciclo de 10 s dispara
// `fetchEstado` y `fetchNotificaciones` juntas, y al entrar salen cinco de una vez. Cuando
// vence el token de acceso de 15 minutos, todas reciben 401 en el mismo instante y cada una
// pedía su propia renovación.
//
// Eso choca de frente con 2.13: el token de refresco **rota** -- cada uso revoca la fila de
// sesión anterior -- así que solo la primera renovación funciona. Las demás presentan un
// token revocado hace milisegundos, reciben 401, y como `renovado` queda en false la
// petición original sigue en 401: el portal lo lee como sesión muerta y echa al usuario.
// Justo lo que este reintento venía a evitar.
//
// Y lo peor no es la molestia: un refresco con token revocado es la señal con la que 2.13
// detecta un token robado. Sin esto, el uso normal del portal se veía igual que un robo.
//
// Medido en producción antes de arreglarlo: 6 h de logs, 7 renovaciones, 1 exitosa.
let refrescoEnCurso = null;

function renovarSesion() {
  if (!refrescoEnCurso) {
    refrescoEnCurso = fetch('https://api.deviant.cl/portal/refresh', {
      method: 'POST',
      credentials: 'include'
    })
      .then(r => r.ok)
      .catch(() => false)
      // Se libera recién cuando terminó, no antes: las que llegaron mientras tanto ya
      // tienen esta misma promesa en la mano y reciben su resultado.
      .finally(() => { refrescoEnCurso = null; });
  }
  return refrescoEnCurso;
}

// Sesión de soporte (4.12): el admin entra con un token en el fragmento de la URL, sin cookie. La
// API lo acepta como `Bearer` (`sesion.js`), pero esta función sólo mandaba cookies, así que con
// «Ver como» toda pantalla que pasa por acá daba 401 y quedaba vacía (24 oct 2026).
let tokenSoporte = null;
export function usarSesionDeSoporte(token) { tokenSoporte = token; }

export async function apiFetch(url, options = {}) {
  if (tokenSoporte) {
    options = { ...options, headers: { Authorization: 'Bearer ' + tokenSoporte, ...options.headers } };
  }

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), TIMEOUT_MS);
  
  // Rescatamos la bandera de modo silencioso (por defecto es false)
  const hideErrorToast = options.hideErrorToast || false;

  try {
    let response = await fetch(url, {
      ...options,
      // La sesión viaja en cookies httpOnly desde 2.13: sin esto el navegador no las
      // manda, porque el portal y la API están en subdominios distintos.
      credentials: 'include',
      signal: controller.signal
    });

    // El token de acceso dura 15 minutos. Un 401 puede ser sesión cerrada o
    // simplemente token vencido: se intenta renovar UNA vez y se reintenta. Sin esto,
    // el portal echaría al usuario cada cuarto de hora.
    // La de soporte no se renueva, a propósito: ver `tokenDeSoporte` en app.js.
    if (response.status === 401 && !tokenSoporte && !options._reintento && !url.endsWith('/portal/refresh')) {
      const renovado = await renovarSesion();
      if (renovado) {
        response = await fetch(url, { ...options, credentials: 'include', signal: controller.signal });
      }
    }

    clearTimeout(id);

    if (!response.ok) {
      const err = new Error(`Error HTTP: ${response.status}`);
      err.status = response.status;
      // El cuerpo del error también viaja. La API responde mensajes pensados para leerse
      // ("ese horario ya no está disponible", "tu perfil no permite cancelar horas"), y sin esto
      // se perdían: el llamador solo veía un número y tenía que inventar un texto propio, casi
      // siempre peor y a veces equivocado. Si el cuerpo no es JSON se sigue de largo, porque
      // fallar al construir el error es la peor forma de fallar.
      try {
        const cuerpo = await response.json();
        err.cuerpo = cuerpo;
        if (cuerpo?.mensaje) err.mensaje = cuerpo.mensaje;
      } catch { /* respuesta sin JSON: queda solo el status */ }
      throw err;
    }

    return await response.json();

  } catch (error) {
    clearTimeout(id);
    
    // Solo disparamos el Toast si NO estamos en modo silencioso
    if (!hideErrorToast) {
      if (error.name === 'AbortError') {
        Toast.error("El servidor está tardando demasiado. Operación cancelada.");
        console.error("[API] Timeout de 10s alcanzado en:", url);
      } else if (!error.status || error.status >= 500) {
        Toast.error("Ocurrió un error de conexión.");
        console.error("[API] Error:", error.message);
      }
    }
    
    throw error;
  }
}